import { SPEECH_RECOGNITION_LANGUAGE_CODES } from '../utils/awsConfig';

export class SpeechToTextService {
  constructor() {
    this.recognition = null;
    this.isListening = false;
    this.onResult = null;
    this.onError = null;
    this.shouldRestart = false;
    this.currentLanguage = 'en';
    this.availableDevices = [];
    this.selectedDeviceId = null;
  }

  // Get available microphone devices
  async getAvailableMicrophones() {
    try {
      // Request permission first to get device labels
      await navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
        stream.getTracks().forEach(track => track.stop());
      });

      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableDevices = devices.filter(device => device.kind === 'audioinput');
      
      console.log('🔵 Available microphones:', this.availableDevices);
      return this.availableDevices.map(device => ({
        deviceId: device.deviceId,
        label: device.label || `Microphone ${device.deviceId.slice(0, 8)}...`
      }));
    } catch (error) {
      console.error('🔴 Error getting microphone devices:', error);
      return [];
    }
  }

  // Set selected microphone device
  setSelectedDevice(deviceId) {
    this.selectedDeviceId = deviceId;
    console.log('🔵 Selected microphone device:', deviceId);
  }

  // Test if speech recognition is available and get supported languages
  static testSpeechRecognition() {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      const isEdge = /Edg/.test(navigator.userAgent);
      const errorMessage = isEdge 
        ? 'Speech recognition not supported in this version of Edge. Please update to the latest version or use Chrome/Safari.'
        : 'Speech recognition not supported in this browser. Please use Chrome, Edge, or Safari.';
      return { supported: false, error: errorMessage };
    }
    return { supported: true };
  }

  // Request microphone permission explicitly
  async requestMicrophonePermission() {
    try {
      console.log('🔵 Requesting microphone permission...');
      
      // Check if we're in Edge and handle differently
      const isEdge = /Edg/.test(navigator.userAgent);
      
      if (isEdge) {
        console.log('🔵 Edge browser detected, using alternative permission request...');
        
        // For Edge, try to check permission state first
        if (navigator.permissions) {
          try {
            const permission = await navigator.permissions.query({ name: 'microphone' });
            console.log('🔵 Current microphone permission state:', permission.state);
            
            if (permission.state === 'denied') {
              throw new Error('Microphone permission denied. Please enable microphone access in Edge settings.');
            }
          } catch (permError) {
            console.log('🔵 Permission query not supported, proceeding with getUserMedia...');
          }
        }
      }
      
      // Try to get user media to trigger permission request
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      console.log('🟢 Microphone permission granted');
      
      // Stop the stream immediately since we just needed permission
      stream.getTracks().forEach(track => track.stop());
      
      return { granted: true };
    } catch (error) {
      console.error('🔴 Microphone permission denied:', error);
      
      let message = 'Microphone access denied.';
      if (error.name === 'NotAllowedError') {
        const isEdge = /Edg/.test(navigator.userAgent);
        if (isEdge) {
          message = 'Microphone access denied in Edge. Please click the microphone icon in the address bar and allow access, then refresh the page.';
        } else {
          message = 'Microphone access denied. Please allow microphone access in your browser settings and refresh the page.';
        }
      } else if (error.name === 'NotFoundError') {
        message = 'No microphone found. Please connect a microphone and try again.';
      } else if (error.name === 'NotReadableError') {
        message = 'Microphone is being used by another application. Please close other apps using the microphone.';
      }
      
      return { granted: false, error: message };
    }
  }
  getBestLanguageCode(language) {
    const preferredCode = SPEECH_RECOGNITION_LANGUAGE_CODES[language];
    
    // Fallback options for each language
    const fallbacks = {
      'en': ['en-US', 'en-GB', 'en'],
      'es': ['es-ES', 'es-US', 'es'],
      'fr': ['fr-FR', 'fr-CA', 'fr'],
      'zh': ['zh-CN', 'zh-TW', 'zh'],
      'ja': ['ja-JP', 'ja'],
      'ar': ['ar-SA', 'ar-EG', 'ar'],
      'th': ['th-TH', 'th']
    };

    const options = fallbacks[language] || [preferredCode, 'en-US'];
    
    // Return the first option (we'll test it when starting)
    return options[0] || 'en-US';
  }

  initialize(language = 'en', onResult, onError) {
    const testResult = SpeechToTextService.testSpeechRecognition();
    if (!testResult.supported) {
      throw new Error(testResult.error);
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.recognition = new SpeechRecognition();
    this.currentLanguage = language;
    
    // Get the best language code
    const speechLangCode = this.getBestLanguageCode(language);
    
    // Configure recognition with more permissive settings
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = speechLangCode;
    this.recognition.maxAlternatives = 1;
    
    // More permissive settings - don't set grammars to null
    // The grammars property expects a SpeechGrammarList, not null
    // We'll just leave it as default instead of trying to set it
    
    console.log(`Initializing speech recognition with language: ${speechLangCode}`);
    
    this.onResult = onResult;
    this.onError = onError;

    // Set up event handlers
    this.recognition.onresult = (event) => {
      console.log('🔵 Speech recognition onresult event fired:', event);
      
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        console.log(`🔵 Result ${i}: "${transcript}", isFinal: ${event.results[i].isFinal}`);
        
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      console.log('🔵 Final transcript:', finalTranscript);
      console.log('🔵 Interim transcript:', interimTranscript);

      if (this.onResult) {
        console.log('🔵 Calling onResult callback');
        this.onResult({
          final: finalTranscript,
          interim: interimTranscript,
          isFinal: finalTranscript.length > 0
        });
      } else {
        console.log('🔴 No onResult callback set!');
      }
    };

    this.recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error, event);
      
      let errorMessage = 'Speech recognition error';
      let shouldRetry = false;
      
      switch (event.error) {
        case 'no-speech':
          errorMessage = 'No speech detected. Please speak louder or check your microphone.';
          shouldRetry = true;
          break;
        case 'audio-capture':
          errorMessage = 'Audio capture failed. Please check your microphone permissions and ensure no other app is using it.';
          break;
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please grant microphone permissions and reload the page.';
          break;
        case 'network':
          errorMessage = 'Network error occurred. Please check your internet connection.';
          shouldRetry = true;
          break;
        case 'language-not-supported':
          // Try to fallback to English
          errorMessage = `Language not fully supported. Switching to English for better compatibility.`;
          this.fallbackToEnglish();
          return;
        case 'service-not-allowed':
          errorMessage = 'Speech recognition service not allowed. Please check your browser settings.';
          break;
        case 'aborted':
          // This is usually not an error, just recognition was stopped
          return;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }
      
      if (this.onError) {
        this.onError(new Error(errorMessage));
      }
      
      // Auto-retry for certain errors
      if (shouldRetry && this.shouldRestart) {
        setTimeout(() => {
          if (this.shouldRestart && this.recognition) {
            try {
              this.recognition.start();
            } catch (error) {
              console.log('Retry failed, stopping auto-restart');
            }
          }
        }, 2000);
      }
    };

    this.recognition.onstart = () => {
      console.log('🟢 Speech recognition started successfully');
      this.isListening = true;
    };

    this.recognition.onend = () => {
      console.log('🔵 Speech recognition ended');
      this.isListening = false;
      
      // Auto-restart if we were supposed to be listening
      if (this.shouldRestart) {
        console.log('🔵 Auto-restarting speech recognition...');
        setTimeout(() => {
          if (this.shouldRestart && this.recognition) {
            try {
              this.recognition.start();
              console.log('🔵 Speech recognition restarted');
            } catch (error) {
              console.error('🔴 Failed to restart speech recognition:', error);
              // Don't throw error here, just log it
            }
          }
        }, 100);
      }
    };

    return true;
  }

  // Fallback to English when other languages fail
  fallbackToEnglish() {
    if (this.currentLanguage !== 'en' && this.recognition) {
      console.log('Falling back to English due to language support issues');
      this.recognition.lang = 'en-US';
      this.currentLanguage = 'en';
      
      // Notify the parent component about the language change
      if (this.onResult) {
        this.onResult({
          final: '',
          interim: '[Switched to English for better compatibility]',
          isFinal: false,
          languageChanged: true
        });
      }
      
      // Try to restart
      if (this.shouldRestart) {
        setTimeout(() => {
          try {
            if (this.recognition) {
              this.recognition.start();
            }
          } catch (error) {
            console.error('Failed to restart with English:', error);
          }
        }, 500);
      }
    }
  }

  async start() {
    if (!this.recognition) {
      throw new Error('Speech recognition not initialized');
    }

    if (this.isListening) {
      console.log('Speech recognition already running');
      return;
    }

    try {
      console.log('🔵 Starting speech recognition...');
      
      // First, explicitly request microphone permission
      const permissionResult = await this.requestMicrophonePermission();
      if (!permissionResult.granted) {
        throw new Error(permissionResult.error);
      }
      
      console.log('🔵 Microphone permission confirmed, starting speech recognition...');
      
      this.shouldRestart = true;
      this.recognition.start();
      console.log('🟢 Speech recognition start command sent');
      
    } catch (error) {
      console.error('🔴 Failed to start speech recognition:', error);
      
      // If it's already started, that's okay
      if (error.message && error.message.includes('already started')) {
        console.log('Speech recognition already running');
        return;
      }
      
      this.shouldRestart = false;
      throw new Error(`Failed to start speech recognition: ${error.message}`);
    }
  }

  stop() {
    if (this.recognition) {
      this.shouldRestart = false;
      if (this.isListening) {
        this.recognition.stop();
        
        // Force cleanup after a short delay to ensure mic is released
        setTimeout(() => {
          if (this.recognition) {
            try {
              this.recognition.abort();
              console.log('🔵 Force aborted speech recognition');
            } catch (e) {
              console.log('🔵 Recognition already stopped');
            }
          }
        }, 100);
      }
      console.log('Stopping speech recognition...');
    }
  }

  changeLanguage(language) {
    const wasListening = this.isListening;
    this.currentLanguage = language;
    
    if (wasListening) {
      this.stop();
    }

    if (this.recognition) {
      const speechLangCode = this.getBestLanguageCode(language);
      this.recognition.lang = speechLangCode;
      console.log(`Changed speech recognition language to: ${speechLangCode}`);
    }

    if (wasListening) {
      setTimeout(() => {
        try {
          this.start();
        } catch (error) {
          console.error('Failed to restart after language change:', error);
          // Try with English as fallback
          if (language !== 'en') {
            this.fallbackToEnglish();
          }
        }
      }, 200);
    }
  }

  // Check if a language is supported (always return true, we'll handle fallbacks)
  isLanguageSupported(language) {
    return true; // We'll handle unsupported languages with fallbacks
  }

  // Get supported languages
  getSupportedLanguages() {
    return Object.keys(SPEECH_RECOGNITION_LANGUAGE_CODES);
  }

  cleanup() {
    this.stop();
    
    // More aggressive cleanup to ensure mic is released
    if (this.recognition) {
      try {
        this.recognition.abort();
        this.recognition.onstart = null;
        this.recognition.onresult = null;
        this.recognition.onerror = null;
        this.recognition.onend = null;
      } catch (e) {
        console.log('🔵 Recognition cleanup completed');
      }
    }
    
    this.recognition = null;
    this.onResult = null;
    this.onError = null;
    this.shouldRestart = false;
    this.isListening = false;
    console.log('🔵 Speech service cleaned up completely');
  }
}
